import { createReducer } from './utils';
import {
  GET_FOCUSES_REQUEST,
  GET_FOCUSES_SUCCESS,
  GET_FOCUSES_FAILURE,
  GET_FOCUSES_FULFILLMENT_PROGRESS_REQUEST,
  GET_FOCUSES_FULFILLMENT_PROGRESS_SUCCESS,
  GET_FOCUSES_FULFILLMENT_PROGRESS_FAILURE,
  RESET_FOCUSES_STATE,
  SNACK_SET,
} from './constants';
import { Focus, FocusName } from '@shared/schema';
import { FocusesState, Action, ThunkAPI } from '../types/state';
import { translate as t } from '@/core/i18n/translate';

const defaultState: FocusesState = {
  focuses: [],
  loading: false,
  errorMessage: null,
};

export const reducer = createReducer(defaultState, {
  [GET_FOCUSES_REQUEST]: handleGetFocusesRequest,
  [GET_FOCUSES_SUCCESS]: handleGetFocusesSuccess,
  [GET_FOCUSES_FAILURE]: handleGetFocusesFailure,
  [GET_FOCUSES_FULFILLMENT_PROGRESS_REQUEST]: handleGetFocusesRequest,
  [GET_FOCUSES_FULFILLMENT_PROGRESS_SUCCESS]: handleGetFocusesFulfillmentProgressSuccess,
  [GET_FOCUSES_FULFILLMENT_PROGRESS_FAILURE]: handleGetFocusesFailure,
  [RESET_FOCUSES_STATE]: () => defaultState,
});

function handleGetFocusesRequest(state: FocusesState): FocusesState {
  return {
    ...state,
    loading: true,
  };
}

function handleGetFocusesSuccess(state: FocusesState, action: Action<unknown>): FocusesState {
  const payload = action.payload as { focuses: Focus[] } | undefined;
  return {
    ...state,
    focuses: payload?.focuses || [],
    loading: false,
    errorMessage: null,
  };
}

function handleGetFocusesFulfillmentProgressSuccess(
  state: FocusesState,
  action: Action<unknown>,
): FocusesState {
  const payload = action.payload as { focuses: Focus[]; globalFulfillmentProgressPct?: number };
  return {
    ...state,
    focuses: payload?.focuses || [],
    globalFulfillmentProgressPct: payload?.globalFulfillmentProgressPct,
    loading: false,
    errorMessage: null,
  };
}

function handleGetFocusesFailure(state: FocusesState, action: Action<unknown>): FocusesState {
  const payload = action.payload as { errorMessage: string } | undefined;
  return {
    ...state,
    loading: false,
    errorMessage: payload?.errorMessage || t('apiFeedback.focuses.fetchError'),
  };
}

export function resetFocusState() {
  return (dispatch: ThunkAPI['dispatch']) => {
    dispatch({ type: RESET_FOCUSES_STATE });
  };
}

export function fetchAllFocuses(versionId: number) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: GET_FOCUSES_REQUEST });
    try {
      const focuses = await dataSource.fetchAllFocuses(versionId);
      dispatch({
        type: GET_FOCUSES_SUCCESS,
        payload: { focuses },
      });
    } catch (error) {
      console.error('Fetch focuses failed:', error);
      dispatch({
        type: GET_FOCUSES_FAILURE,
        payload: { errorMessage: (error as Error).message || t('apiFeedback.focuses.fetchError') },
      });
      const snack = {
        open: true,
        message: t('apiFeedback.focuses.fetchError'),
        severity: 'error',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
    }
  };
}

export function fetchFocusesFulfillmentProgress(fulfillmentVersionId: number) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: GET_FOCUSES_FULFILLMENT_PROGRESS_REQUEST });
    try {
      const { focuses, globalFulfillmentProgressPct } =
        await dataSource.fetchFocusesFulfillmentProgress(fulfillmentVersionId);
      dispatch({
        type: GET_FOCUSES_FULFILLMENT_PROGRESS_SUCCESS,
        payload: { focuses, globalFulfillmentProgressPct },
      });
    } catch (error) {
      console.error('Fetch focuses fulfillment progress failed:', error);
      dispatch({
        type: GET_FOCUSES_FULFILLMENT_PROGRESS_FAILURE,
        payload: {
          errorMessage: (error as Error).message || t('apiFeedback.focuses.progressFetchError'),
        },
      });
      const snack = {
        open: true,
        message: t('apiFeedback.focuses.progressFetchError'),
        severity: 'error',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
    }
  };
}

export function fetchFocusNamesByIds(
  focusIds: number[],
  params?: { planId?: number; year?: number },
) {
  return async (
    _dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ): Promise<FocusName[]> => {
    try {
      return await dataSource.getFocusNamesByIds(focusIds, params);
    } catch (error) {
      console.error('Fetch focus names failed:', error);
      return [];
    }
  };
}
